import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  UserPlus,
  PhoneCall,
  BarChart3,
  Settings,
  TrendingUp,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import healthcareImg from "../../assets/healthcare.jpg";
// NOTE: adjust this path to wherever Seo.js lives in your project
import Seo, { SITE_URL } from "../Seo";
import { ROUTES } from "../../seoData";

const PATH = "/solutions/healthcare";
const TITLE = ROUTES[PATH].title;
const DESCRIPTION = ROUTES[PATH].description;

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "LMSBOTS Healthcare CRM",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Healthcare CRM / Lead Management",
    operatingSystem: "Web, Android, iOS",
    description: DESCRIPTION,
    url: `${SITE_URL}${PATH}`,
    publisher: { "@id": `${SITE_URL}/#organization` },
    featureList: [
      "Omnichannel patient inquiry capture",
      "Intelligent routing to departments and consultants",
      "Automated SMS, email and WhatsApp appointment reminders",
      "Patient acquisition and conversion analytics",
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Solutions", item: `${SITE_URL}/solutions` },
      { "@type": "ListItem", position: 3, name: "Healthcare", item: `${SITE_URL}${PATH}` },
    ],
  },
];

// Static content lives outside the component so it isn't rebuilt on every render
const HERO_POINTS = ["Patient inquiry capture", "Automated reminders", "Department routing"];

const FEATURES = [
  {
    icon: UserPlus,
    title: "Omnichannel Patient Capture",
    desc: "Automatically aggregate inquiries from web portals, social campaigns, direct calls, and integrated messaging platforms into a unified dashboard.",
  },
  {
    icon: Settings,
    title: "Intelligent Routing Engine",
    desc: "Route patient leads instantly to the right specialized department or available consultant using smart assignment rules.",
  },
  {
    icon: PhoneCall,
    title: "Automated Nurturing Workflows",
    desc: "Reduce no-shows and increase engagement with trigger-based SMS, email, and WhatsApp appointment reminders and follow-ups.",
  },
  {
    icon: BarChart3,
    title: "Performance & ROI Analytics",
    desc: "Get clear visibility into patient acquisition costs, departmental conversion rates, and overall pipeline health.",
  },
];

const STEPS = [
  {
    icon: UserPlus,
    title: "Centralized Capture",
    desc: "Aggregate prospect data securely into your unified CRM.",
  },
  {
    icon: Settings,
    title: "Smart Automation",
    desc: "Trigger intelligent assignments and follow-up sequences.",
  },
  {
    icon: TrendingUp,
    title: "Maximum Conversion",
    desc: "Turn qualified inquiries into confirmed consultations.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } },
};

// Shared section heading: light weight with a gradient-highlighted phrase
const SectionHeading = ({ id, pill, before, highlight }) => (
  <div className="mx-auto mb-12 max-w-3xl text-center lg:mb-16">
    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3 py-1 text-xs font-medium text-[#48247f] shadow-sm">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#12dfbd]" />
      {pill}
    </div>
    <h2
      id={id}
      className="text-balance text-[26px] font-light leading-[1.25] tracking-tight text-[#28194f] sm:text-[34px] lg:text-[38px]"
    >
      {before}{" "}
      <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
        {highlight}
      </span>
    </h2>
    <div className="mx-auto mt-6 h-1 w-24 rounded-full bg-gradient-to-r from-[#12dfbd] via-[#702cb0] to-[#48247f]" />
  </div>
);

const Healthcare = () => {
  const reduced = useReducedMotion();
  const item = reduced ? undefined : fadeUp;
  const group = reduced ? undefined : stagger;
  const initial = reduced ? false : "hidden";

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path={PATH} jsonLd={JSON_LD} />

      <div className="w-full overflow-x-clip bg-white font-sans text-[#28194f] antialiased">
        {/* ===== HERO (light surface, same typography and components as the home hero) ===== */}
        <section
          id="healthcare-hero"
          aria-labelledby="healthcare-heading"
          className="relative isolate w-full overflow-hidden bg-gradient-to-br from-[#fbf7ff] via-white to-[#effbf8]"
        >
          <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-[#702cb0]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-[#12dfbd]/15 blur-3xl" />

          <div className="relative z-10 mx-auto grid w-full max-w-[1500px] items-center gap-10 px-4 pb-14 pt-24 sm:gap-12 sm:px-10 sm:pb-16 sm:pt-28 lg:grid-cols-[1fr_1fr] lg:gap-14 lg:px-14 lg:pb-20 lg:pt-32 xl:px-20">
            {/* Left content */}
            <motion.div
              initial={initial}
              animate="visible"
              variants={group}
              className="mx-auto max-w-xl text-center lg:mx-0 lg:text-left"
            >
              <motion.div
                variants={item}
                className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#48247f]/15 bg-white px-3 py-1 text-xs font-medium text-[#48247f] shadow-sm"
              >
                <ShieldCheck className="h-3.5 w-3.5 text-[#702cb0]" />
                Enterprise Healthcare CRM
              </motion.div>

              <motion.h1
                id="healthcare-heading"
                variants={item}
                className="text-balance text-[28px] font-light leading-[1.25] tracking-tight sm:text-[34px] lg:text-[38px] xl:text-[42px]"
              >
                Optimize patient acquisition{" "}
                <span className="bg-gradient-to-r from-[#48247f] to-[#702cb0] bg-clip-text font-normal text-transparent">
                  intelligently &amp; securely
                </span>
              </motion.h1>

              <motion.p
                variants={item}
                className="mx-auto mt-4 max-w-[540px] text-sm font-light leading-[1.75] text-[#3b2a63] sm:text-base lg:mx-0 lg:text-[17px]"
              >
                Centralize patient inquiries, deploy automated nurturing
                workflows, and convert prospects into confirmed consultations
                with the LMSBOTS healthcare CRM built for hospitals and clinics.
              </motion.p>

              <motion.div
                variants={item}
                className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center lg:justify-start lg:gap-4"
              >
                <Link
                  to="/contact"
                  className="group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-[#12dfbd] px-6 py-3 text-xs font-semibold text-[#102b37] shadow-lg shadow-[#12dfbd]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#40f0d3] hover:shadow-[#12dfbd]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2 sm:text-sm"
                >
                  Request Platform Demo
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/features"
                  className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-[#48247f]/20 bg-white px-6 py-3 text-xs font-medium text-[#48247f] shadow-sm transition-all duration-300 hover:border-[#702cb0]/50 hover:bg-[#f1ecfa] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2 sm:text-sm"
                >
                  Explore Features
                </Link>
              </motion.div>

              <motion.ul
                variants={item}
                className="mt-8 flex flex-wrap items-center justify-center gap-x-5 gap-y-2.5 lg:justify-start"
              >
                {HERO_POINTS.map((p) => (
                  <li key={p} className="inline-flex items-center gap-2 text-xs font-medium text-[#3b2a63] sm:text-sm">
                    <CheckCircle2 className="h-4 w-4 text-[#0f9d86]" strokeWidth={2.5} />
                    {p}
                  </li>
                ))}
              </motion.ul>
            </motion.div>

            {/* Right image — simple & clean, matching Real Estate */}
            <motion.div
              initial={reduced ? false : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              className="relative mx-auto w-full max-w-[600px] px-3 pb-6 pt-3 lg:max-w-none"
            >
              {/* Plain tinted block sits below the image */}
              <div className="absolute inset-x-8 bottom-0 top-10 rounded-3xl bg-[#e3faf5]" />
              <div className="relative rounded-2xl bg-white p-2 shadow-xl ring-1 ring-slate-200">
                <img
                  src={healthcareImg}
                  alt="LMSBOTS healthcare CRM dashboard showing patient inquiries, appointment follow-ups and department routing"
                  width="1200"
                  height="800"
                  fetchpriority="high"
                  decoding="async"
                  className="h-auto w-full rounded-xl border border-slate-200 object-cover"
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* ===== FEATURES (white surface) ===== */}
        <section
          id="healthcare-features"
          aria-labelledby="healthcare-features-heading"
          className="relative w-full bg-white py-14 sm:py-16 lg:py-24"
        >
          <div className="mx-auto w-full max-w-[1300px] px-4 sm:px-10 lg:px-14">
            <motion.div
              initial={reduced ? false : { opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SectionHeading
                id="healthcare-features-heading"
                pill="Key Features"
                before="Advanced features for"
                highlight="medical facilities"
              />
            </motion.div>

            <motion.ul
              variants={group}
              initial={initial}
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-8"
            >
              {FEATURES.map(({ icon: Icon, title, desc }, i) => (
                <motion.li
                  key={title}
                  variants={item}
                  className="group relative flex gap-5 overflow-hidden rounded-2xl border border-[#48247f]/10 bg-white p-6 shadow-[0_8px_30px_-12px_rgba(72,36,127,0.25)] transition-all duration-300 hover:-translate-y-1 hover:border-[#702cb0]/40 hover:shadow-[0_24px_48px_-12px_rgba(72,36,127,0.4)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:p-8"
                >
                  {/* Left accent bar: reveals on hover */}
                  <div className="absolute left-0 top-0 h-full w-1 origin-top scale-y-0 bg-gradient-to-b from-[#12dfbd] via-[#702cb0] to-[#48247f] transition-transform duration-500 ease-out group-hover:scale-y-100" />

                  <div className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#f1ecfa] text-[#48247f] transition-all duration-300 group-hover:-rotate-6 group-hover:bg-gradient-to-br group-hover:from-[#48247f] group-hover:to-[#702cb0] group-hover:text-white">
                    <Icon className="h-6 w-6" />
                  </div>

                  <div className="min-w-0">
                    <div className="mb-2 flex items-start justify-between gap-3">
                      <h3 className="text-lg font-medium tracking-tight text-[#28194f] sm:text-xl">
                        {title}
                      </h3>
                      <span className="text-2xl font-light leading-none text-[#48247f]/15 transition-colors duration-300 group-hover:text-[#12dfbd]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <p className="text-sm font-normal leading-[1.75] text-slate-700">{desc}</p>
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* ===== HOW IT WORKS (lavender surface) ===== */}
        <section
          id="healthcare-workflow"
          aria-labelledby="healthcare-workflow-heading"
          className="relative isolate w-full overflow-hidden border-t border-[#48247f]/10 bg-[#f6f3fc] py-14 sm:py-16 lg:py-24"
        >
          <div className="pointer-events-none absolute -bottom-24 -right-24 -z-10 h-96 w-96 rounded-full bg-[#12dfbd]/15 blur-3xl" />

          <div className="mx-auto w-full max-w-[1300px] px-4 sm:px-10 lg:px-14">
            <motion.div
              initial={reduced ? false : { opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <SectionHeading
                id="healthcare-workflow-heading"
                pill="How It Works"
                before="Streamlined"
                highlight="operational flow"
              />
            </motion.div>

            <motion.ol
              variants={group}
              initial={initial}
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="relative grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8"
            >
              {/* Connecting line for desktop */}
              <div
                aria-hidden="true"
                className="absolute left-[16%] right-[16%] top-[3.25rem] hidden h-px bg-gradient-to-r from-transparent via-[#702cb0]/40 to-transparent md:block"
              />

              {STEPS.map(({ icon: Icon, title, desc }, i) => (
                <motion.li
                  key={title}
                  variants={item}
                  className="group relative z-10 rounded-2xl border border-[#48247f]/10 bg-white p-6 text-center shadow-[0_8px_30px_-12px_rgba(72,36,127,0.25)] transition-all duration-300 hover:-translate-y-1 hover:border-[#702cb0]/40 hover:shadow-[0_24px_48px_-12px_rgba(72,36,127,0.4)] motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:p-8"
                >
                  <div className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#f1ecfa] text-[#48247f] transition-all duration-300 group-hover:bg-gradient-to-br group-hover:from-[#48247f] group-hover:to-[#702cb0] group-hover:text-white">
                    <Icon className="h-7 w-7" />
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-[#12dfbd] text-xs font-semibold text-[#102b37] shadow">
                      {i + 1}
                    </span>
                  </div>
                  <h3 className="mb-2 text-lg font-medium tracking-tight text-[#28194f] sm:text-xl">
                    {title}
                  </h3>
                  <p className="text-sm leading-[1.7] text-slate-700">{desc}</p>
                </motion.li>
              ))}
            </motion.ol>

            {/* Closing call to action */}
            <div className="mt-12 text-center lg:mt-16">
              <Link
                to="/contact"
                className="group inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#48247f] to-[#702cb0] px-7 py-3 text-xs font-semibold text-white shadow-lg shadow-[#48247f]/25 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[#48247f]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#48247f] focus-visible:ring-offset-2 sm:text-sm"
              >
                Book a Healthcare Demo
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Healthcare;